import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classes from './OrderMarkingPopup.module.css';
import { getCsrfToken } from '../../../../utils/helpers';

export type OrderMarkingRow = {
    cartIndex: number;
    lineId?: string;
    quantity: number;
    name: string;
    articleNumber: string;
    sizeOrColor: string;
    initialMarkingCodes: string[];
};
type SavedResult = {
    orderStatus?: string;
    fullyMarked?: boolean;
};
type Props = {
    orderId: string;
    orderNumber: string;
    apiBase: string;
    rows: OrderMarkingRow[];
    onClose: () => void;
    onSaved: (result?: SavedResult) => void;
};

const GS = String.fromCharCode(29); // Group Separator (FNC1)
const SEP = ' ';

/**
 * Нормализация артикула (для сравнения как ключ):
 * убираем пробелы/дефисы/нижние подчёркивания, upper.
 */
const normalizeArticleKey = (v: string): string => {
    return String(v ?? '')
        .trim()
        .toUpperCase()
        .replace(/[\s\-_]+/g, '');
};

const AI_PREFIXES = new Set(['10', '11', '17', '21', '30', '37', '91', '92', '93', '94', '95', '96', '97', '98', '99']);

const isAlphaNum = (ch: string): boolean => /[0-9A-Za-z]/.test(ch);

const startsWithAiAt = (s: string, idx: number): boolean => {
    if (idx < 0 || idx + 2 > s.length) return false;
    return AI_PREFIXES.has(s.slice(idx, idx + 2));
};

const collapseSpaces = (s: string): string => s.replace(/\s+/g, ' ').trim();

/**
 * Некоторые сканеры/раскладки/драйверы "портят" FNC1/GS,
 * заменяя его на печатные символы: _ ! ; < . , (иногда и пробел).
 *
 * ВАЖНО: нельзя бездумно заменять эти символы везде (они могут быть частью серийника/хвоста),
 * поэтому делаем замену ТОЛЬКО когда справа начинается AI (две цифры: 91/92/… и т.п.).
 *
 * Возвращаем не GS, а ПРОБЕЛ, потому что codeFullNorm в Mongo хранится с пробелами.
 */
const coercePseudoSeparatorsToSep = (input: string): string => {
    const s = String(input ?? '');
    if (!s) return '';
    const pseudo = new Set(['_', '!', ';', '<', '.', ',']);
    let out = '';
    for (let i = 0; i < s.length; i += 1) {
        const ch = s[i];
        if (!pseudo.has(ch)) {
            out += ch;
            continue;
        }
        // пропускаем пачку псевдо-разделителей подряд
        let j = i + 1;
        while (j < s.length && pseudo.has(s[j])) j += 1;
        const prev = i > 0 ? s[i - 1] : '';
        const prevOk = prev ? isAlphaNum(prev) : false;
        const nextStartsAi = startsWithAiAt(s, j);
        if (prevOk && nextStartsAi) {
            out += SEP; // пробел как канонический разделитель под codeFullNorm
            i = j - 1;
            continue;
        }
        // иначе оставляем как есть (могло быть частью данных)
        out += ch;
    }
    return out;
};

/**
 * Каноническая нормализация:
 * - убираем хвостовые CR/LF
 * - табы/GS -> SEP (пробел)
 * - эвристически восстанавливаем разделители из псевдо-символов
 * - trim + схлопываем пробелы
 * - убираем префикс ]d2
 * - upper (т.к. codeFullNorm у тебя uppercase)
 */
const normalizeScan = (v: string): string => {
    let s = String(v ?? '');
    s = s.replace(/[\r\n]+$/g, '');
    s = s.replace(/\t/g, SEP);
    s = s.split(GS).join(SEP);
    s = coercePseudoSeparatorsToSep(s);
    s = s.trim();
    if (s.startsWith(']d2')) s = s.slice(3);
    s = collapseSpaces(s).toUpperCase();
    return s;
};

const looksLikeChz = (v: string): boolean => {
    const s = normalizeScan(v);
    if (!s) return false;
    if (s.length < 10 || s.length > 512) return false;
    return /\d/.test(s);
};

const normalizeCodesArray = (arr: string[]): string[] => {
    return arr.map((x) => normalizeScan(x)).filter((x) => x.length > 0);
};

const arraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

const splitMaybeMultipleCodes = (raw: string): string[] => {
    const s = String(raw ?? '');
    const manualParts = s
        .split(/[;,]+/g)
        .map((x) => normalizeScan(x))
        .filter((x) => x.length > 0);
    if (manualParts.length > 1) return manualParts;
    const one = normalizeScan(s);
    return one ? [one] : [];
};

// ---- Серверная валидация кода по crpt_codes + сверка артикула ----

type CodeValidationStatus = 'pending' | 'ok' | 'not_found' | 'article_mismatch' | 'bad_format' | 'error';

type CodeValidation = {
    status: CodeValidationStatus;
    expectedArticleKey: string;
    foundArticleKey?: string;
    message?: string;
};

type CrptSearchItem = {
    codeFullNorm?: unknown;
    codeShortNorm?: unknown;
    articleNorm?: unknown;
    articleNumber?: unknown;
    article?: unknown;
    status?: unknown;
};

const readString = (v: unknown): string => (typeof v === 'string' ? v : '');

const pickArticleKeyFromCrptItem = (it: CrptSearchItem): string => {
    const a = readString(it.articleNorm) || readString(it.articleNumber) || readString(it.article) || '';
    return normalizeArticleKey(a);
};

const pickCodeFull = (it: CrptSearchItem): string => readString(it.codeFullNorm).toUpperCase();
const pickCodeShort = (it: CrptSearchItem): string => readString(it.codeShortNorm).toUpperCase();

const OrderMarkingPopup: React.FC<Props> = ({ orderId, orderNumber, apiBase, rows, onClose, onSaved }) => {
    const [activeCartIndex, setActiveCartIndex] = useState<number | null>(null);

    const [valuesByIndex, setValuesByIndex] = useState<Record<number, string[]>>(() => {
        const init: Record<number, string[]> = {};
        for (const r of rows) init[r.cartIndex] = normalizeCodesArray(r.initialMarkingCodes ?? []);
        return init;
    });

    const initialByIndex = useMemo(() => {
        const init: Record<number, string[]> = {};
        for (const r of rows) init[r.cartIndex] = normalizeCodesArray(r.initialMarkingCodes ?? []);
        return init;
    }, [rows]);

    const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});
    const [error, setError] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);

    const [validationByCode, setValidationByCode] = useState<Record<string, CodeValidation>>({});
    const validationCacheRef = useRef<Record<string, CodeValidation>>({});
    const validateReqIdRef = useRef<Record<string, number>>({}); // пер-кодовые requestId
    const validateReqSeqRef = useRef<number>(0); // общий счётчик id

    const expectedArticleKeyByCartIndex = useMemo(() => {
        const map: Record<number, string> = {};
        for (const r of rows) {
            map[r.cartIndex] = normalizeArticleKey(r.articleNumber || '');
        }
        return map;
    }, [rows]);

    const hasChanges = useMemo(() => {
        return rows.some((r) => {
            const current = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
            const initial = normalizeCodesArray(initialByIndex[r.cartIndex] ?? []);
            return !arraysEqual(current, initial);
        });
    }, [rows, valuesByIndex, initialByIndex]);

    const duplicates = useMemo(() => {
        const counts = new Map<string, number>();
        for (const r of rows) {
            const arr = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
            for (const code of arr) counts.set(code, (counts.get(code) ?? 0) + 1);
        }
        const dup = new Set<string>();
        counts.forEach((c, k) => {
            if (c > 1) dup.add(k);
        });
        return dup;
    }, [rows, valuesByIndex]);

    const overfilledRows = useMemo(() => {
        return rows.filter((r) => {
            const arr = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
            const qty = Math.max(0, Math.trunc(r.quantity || 0));
            return qty > 0 && arr.length > qty;
        });
    }, [rows, valuesByIndex]);

    const hasPendingValidation = useMemo(() => {
        for (const r of rows) {
            const arr = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
            for (const code of arr) {
                if (validationByCode[code]?.status === 'pending') return true;
            }
        }
        return false;
    }, [rows, valuesByIndex, validationByCode]);

    const invalidCodes = useMemo(() => {
        const bad: Array<{ cartIndex: number; code: string; reason: string }> = [];
        for (const r of rows) {
            const arr = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
            for (const code of arr) {
                if (!looksLikeChz(code)) {
                    bad.push({ cartIndex: r.cartIndex, code, reason: 'bad_format' });
                    continue;
                }
                const v = validationByCode[code];
                if (!v) continue;
                if (v.status === 'pending') bad.push({ cartIndex: r.cartIndex, code, reason: 'pending' });
                if (v.status === 'not_found') bad.push({ cartIndex: r.cartIndex, code, reason: 'not_found' });
                if (v.status === 'article_mismatch') bad.push({ cartIndex: r.cartIndex, code, reason: 'article_mismatch' });
                if (v.status === 'error' || v.status === 'bad_format') bad.push({ cartIndex: r.cartIndex, code, reason: 'error' });
            }
        }
        return bad;
    }, [rows, valuesByIndex, validationByCode]);

    const canSave = hasChanges && !saving && duplicates.size === 0 && invalidCodes.length === 0 && overfilledRows.length === 0 && !hasPendingValidation;

    const pendingRef = useRef<{ cartIndex: number; head: string; ts: number } | null>(null);
    const pendingTimerRef = useRef<number | null>(null);

    const clearPending = () => {
        if (pendingTimerRef.current != null) window.clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
        pendingRef.current = null;
    };

    const looksLikeGs1Head = (s: string) => /^01\d{10,16}21/.test(s);
    const looksLikeCryptoTail = (s: string) => /^[A-Za-z0-9+/_=-]{20,}$/.test(s) && !/^01\d/.test(s);

    useEffect(() => {
        return () => {
            clearPending();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeCartIndex == null) return;
        const el = inputRefs.current[activeCartIndex];
        if (!el) return;
        el.focus();
        try {
            el.setSelectionRange(0, el.value.length);
        } catch {
            // ignore
        }
    }, [activeCartIndex]);

    const safeClose = () => {
        clearPending();
        onClose();
    };

    useEffect(() => {
        const onDocKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            safeClose();
        };
        document.addEventListener('keydown', onDocKeyDown);
        return () => document.removeEventListener('keydown', onDocKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onClose]);

    const activateRow = (cartIndex: number) => {
        setError('');
        setActiveCartIndex(cartIndex);
    };

    const setCodes = (cartIndex: number, next: string[]) => {
        const normalized = normalizeCodesArray(next);
        setValuesByIndex((prev) => ({ ...prev, [cartIndex]: normalized }));
    };

    const clearAllCodes = (cartIndex: number) => {
        setError('');
        clearPending();
        setCodes(cartIndex, []);
        setActiveCartIndex(cartIndex);
    };

    const removeOneCode = (cartIndex: number, code: string) => {
        setError('');
        const cur = normalizeCodesArray(valuesByIndex[cartIndex] ?? []);
        const next = cur.filter((x) => x !== code);
        setCodes(cartIndex, next);
        setActiveCartIndex(cartIndex);
    };

    const getNextIncompleteCartIndex = (fromCartIndex: number, nextValuesByIndex: Record<number, string[]>): number | null => {
        const fromPos = rows.findIndex((r) => r.cartIndex === fromCartIndex);
        if (fromPos < 0) return null;
        for (let i = fromPos + 1; i < rows.length; i += 1) {
            const r = rows[i];
            const qty = Math.max(0, Math.trunc(r.quantity || 0));
            if (qty <= 0) continue;
            const codes = normalizeCodesArray(nextValuesByIndex[r.cartIndex] ?? []);
            if (codes.length < qty) return r.cartIndex;
        }
        return null;
    };

    const fetchCrptSearch = async (q: string): Promise<CrptSearchItem[]> => {
        const csrf = await getCsrfToken();
        const url = `${apiBase}/crpt/search?q=${encodeURIComponent(q)}&skip=0&limit=10`;
        const resp = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'csrf-token': csrf,
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        const data: unknown = await resp.json().catch(() => null);

        if (!resp.ok) {
            const msg =
                typeof (data as { error?: unknown } | null)?.error === 'string'
                    ? (data as { error: string }).error
                    : `CRPT search error: ${resp.status} ${resp.statusText}`;
            throw new Error(msg);
        }

        if (Array.isArray(data)) return data as CrptSearchItem[];
        const maybeItems = (data as any)?.items;
        if (Array.isArray(maybeItems)) return maybeItems as CrptSearchItem[];
        const maybeList = (data as any)?.list;
        if (Array.isArray(maybeList)) return maybeList as CrptSearchItem[];
        return [];
    };

    const ensureCodeValidated = async (code: string, expectedArticleKey: string): Promise<void> => {
        const normalized = normalizeScan(code);
        if (!normalized) return;

        if (!looksLikeChz(normalized)) {
            const val: CodeValidation = {
                status: 'bad_format',
                expectedArticleKey,
                message: 'Код не похож на маркировку Честного знака',
            };
            validationCacheRef.current[normalized] = val;
            setValidationByCode((prev) => ({ ...prev, [normalized]: val }));
            return;
        }

        const cached = validationCacheRef.current[normalized];
        if (cached && cached.status !== 'pending' && cached.expectedArticleKey === expectedArticleKey) {
            setValidationByCode((prev) => ({ ...prev, [normalized]: cached }));
            return;
        }

        const reqId = (validateReqSeqRef.current += 1);
        validateReqIdRef.current[normalized] = reqId;

        const pendingVal: CodeValidation = { status: 'pending', expectedArticleKey };
        validationCacheRef.current[normalized] = pendingVal;
        setValidationByCode((prev) => ({ ...prev, [normalized]: pendingVal }));

        try {
            const items = await fetchCrptSearch(normalized);

            // если за это время стартовала новая проверка для этого же кода - игнорируем старый ответ
            if (validateReqIdRef.current[normalized] !== reqId) return;

            const hit = items.find((it) => {
                const full = pickCodeFull(it);
                const short = pickCodeShort(it);
                return full === normalized || short === normalized;
            });

            if (!hit) {
                const val: CodeValidation = {
                    status: 'not_found',
                    expectedArticleKey,
                    message: 'Код маркировки не найден в базе CRPT',
                };
                validationCacheRef.current[normalized] = val;
                setValidationByCode((prev) => ({ ...prev, [normalized]: val }));
                return;
            }

            const foundArticleKey = pickArticleKeyFromCrptItem(hit);

            if (expectedArticleKey && foundArticleKey && expectedArticleKey !== foundArticleKey) {
                const val: CodeValidation = {
                    status: 'article_mismatch',
                    expectedArticleKey,
                    foundArticleKey,
                    message: `Код относится к другому артикулу (ожидался ${expectedArticleKey}, найден ${foundArticleKey})`,
                };
                validationCacheRef.current[normalized] = val;
                setValidationByCode((prev) => ({ ...prev, [normalized]: val }));
                return;
            }

            const ok: CodeValidation = { status: 'ok', expectedArticleKey, foundArticleKey };
            validationCacheRef.current[normalized] = ok;
            setValidationByCode((prev) => ({ ...prev, [normalized]: ok }));
        } catch (e) {
            if (validateReqIdRef.current[normalized] !== reqId) return;
            const val: CodeValidation = {
                status: 'error',
                expectedArticleKey,
                message: e instanceof Error ? e.message : 'Ошибка валидации кода через CRPT',
            };
            validationCacheRef.current[normalized] = val;
            setValidationByCode((prev) => ({ ...prev, [normalized]: val }));
        }
    };

    const scheduleValidationForCodes = (cartIndex: number, codes: string[]) => {
        const expectedKey = expectedArticleKeyByCartIndex[cartIndex] || '';
        for (const c of codes) {
            void ensureCodeValidated(c, expectedKey);
        }
    };

    // при открытии попапа проверяем уже существующие коды (initialMarkingCodes)
    useEffect(() => {
        for (const r of rows) {
            const codes = normalizeCodesArray(initialByIndex[r.cartIndex] ?? []);
            if (codes.length > 0) scheduleValidationForCodes(r.cartIndex, codes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const appendCodes = (cartIndex: number, incoming: string[]) => {
        const row = rows.find((r) => r.cartIndex === cartIndex);
        if (!row) return;

        const qty = Math.max(0, Math.trunc(row.quantity || 0));

        // snapshot, чтобы при быстрых сканах не ловить рассинхрон
        const baseValues = valuesByIndex;
        const current = normalizeCodesArray(baseValues[cartIndex] ?? []);
        const toAdd = normalizeCodesArray(incoming);
        if (toAdd.length === 0) return;

        const existing = new Set<string>();
        for (const r of rows) {
            const arr = normalizeCodesArray(baseValues[r.cartIndex] ?? []);
            if (r.cartIndex === cartIndex) continue;
            for (const c of arr) existing.add(c);
        }
        for (const c of current) existing.add(c);

        let changed = false;
        let localError = '';
        const next = [...current];
        const newlyAdded: string[] = [];

        for (const code of toAdd) {
            if (!looksLikeChz(code)) {
                localError = 'Код не похож на маркировку Честного знака (код выглядит странно).';
                break;
            }
            if (existing.has(code)) {
                localError = 'Этот код уже указан в другой позиции заказа. Коды должны быть уникальны.';
                break;
            }
            if (qty > 0 && next.length >= qty) {
                localError = `Лишний код: для этой позиции уже достаточно (${next.length}/${qty}).`;
                break;
            }
            next.push(code);
            newlyAdded.push(code);
            existing.add(code);
            changed = true;
        }

        if (changed) {
            setCodes(cartIndex, next);
            scheduleValidationForCodes(cartIndex, newlyAdded);
        }

        if (localError) {
            setError(localError);
            return;
        }

        setError('');
        if (qty > 0 && next.length === qty) {
            const nextValuesByIndex: Record<number, string[]> = { ...baseValues, [cartIndex]: next };
            const nextIdx = getNextIncompleteCartIndex(cartIndex, nextValuesByIndex);
            setActiveCartIndex(nextIdx);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, cartIndex: number) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            safeClose();
            return;
        }

        if (e.key === 'Backspace' && e.currentTarget.value.length === 0) {
            const cur = normalizeCodesArray(valuesByIndex[cartIndex] ?? []);
            const last = cur[cur.length - 1];
            if (last) removeOneCode(cartIndex, last);
            return;
        }

        if (e.key !== 'Enter') return;
        e.preventDefault();

        const raw = e.currentTarget.value;
        e.currentTarget.value = '';

        if (/[\r\n]/.test(raw.replace(/[\r\n]+$/g, ''))) {
            setError('Сканер передал перенос строки внутри кода. Проверьте настройку разделителей/GS (FNC1).');
            e.currentTarget.value = '';
            return;
        }

        const normalized = normalizeScan(raw);
        if (!normalized) return;

        // eslint-disable-next-line no-console
        console.log('RAW:', JSON.stringify(raw));
        // eslint-disable-next-line no-console
        console.log(
            'CODES:',
            Array.from(raw).map((ch) => ch.charCodeAt(0)),
        );
        // eslint-disable-next-line no-console
        console.log(
            'NORMALIZED:',
            JSON.stringify(normalized),
            'NORM_CODES:',
            Array.from(normalized).map((ch) => ch.charCodeAt(0)),
        );

        // (1) head + tail (если сканер присылает частями)
        const pending = pendingRef.current;
        if (pending && pending.cartIndex === cartIndex && Date.now() - pending.ts < 1500 && looksLikeCryptoTail(normalized)) {
            const merged = `${pending.head}${SEP}${normalized}`; // пробел, а не GS
            clearPending();
            appendCodes(cartIndex, [merged]);
            return;
        }

        // (2) пришла "голова" - ждём хвост
        if (looksLikeGs1Head(normalized)) {
            clearPending();
            pendingRef.current = { cartIndex, head: normalized, ts: Date.now() };
            pendingTimerRef.current = window.setTimeout(() => {
                const p = pendingRef.current;
                if (p && p.cartIndex === cartIndex && p.head === normalized) {
                    pendingRef.current = null;
                    appendCodes(cartIndex, [normalized]);
                }
            }, 900);
            return;
        }

        // (3) обычный путь
        clearPending();
        const parsed = splitMaybeMultipleCodes(normalized);
        appendCodes(cartIndex, parsed);
    };

    const handleSave = async () => {
        if (!canSave) return;

        setSaving(true);
        setError('');
        clearPending();

        try {
            const changes = rows
                .map((r) => {
                    const current = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
                    const initial = normalizeCodesArray(initialByIndex[r.cartIndex] ?? []);
                    if (arraysEqual(current, initial)) return null;
                    return {
                        cartIndex: r.cartIndex,
                        lineId: r.lineId ?? undefined,
                        markingCodes: current,
                    };
                })
                .filter((x): x is { cartIndex: number; lineId: string | undefined; markingCodes: string[] } => x !== null);

            if (changes.length === 0) {
                safeClose();
                return;
            }

            const csrfToken = await getCsrfToken();
            const resp = await fetch(`${apiBase}/orders/${encodeURIComponent(orderId)}/marking`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'csrf-token': csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ items: changes }),
            });

            const data: unknown = await resp.json().catch(() => null);

            if (!resp.ok) {
                const msg =
                    typeof (data as { error?: unknown } | null)?.error === 'string'
                        ? (data as { error: string }).error
                        : `Ошибка сохранения: ${resp.status} ${resp.statusText}`;
                throw new Error(msg);
            }

            const result: SavedResult | undefined =
                data && typeof data === 'object'
                    ? {
                          orderStatus:
                              typeof (data as { orderStatus?: unknown }).orderStatus === 'string' ? (data as { orderStatus: string }).orderStatus : undefined,
                          fullyMarked:
                              typeof (data as { fullyMarked?: unknown }).fullyMarked === 'boolean' ? (data as { fullyMarked: boolean }).fullyMarked : undefined,
                      }
                    : undefined;

            onSaved(result);
            safeClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Неизвестная ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        if (activeCartIndex == null) return;
        const el = inputRefs.current[activeCartIndex];
        el?.focus();
    };

    const validationErrorSummary = useMemo(() => {
        const errs: string[] = [];

        const codesInUse = new Set<string>();
        for (const r of rows) {
            const arr = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
            for (const c of arr) codesInUse.add(c);
        }

        for (const c of Array.from(codesInUse)) {
            const v = validationByCode[c];
            if (!v) continue;
            if (v.status === 'not_found') errs.push(`Код не найден: ${c.length > 28 ? `${c.slice(0, 28)}…` : c}`);
            if (v.status === 'article_mismatch') errs.push(`Код не соответствует артикулу: ${c.length > 28 ? `${c.slice(0, 28)}…` : c}`);
            if (v.status === 'error') errs.push(`Ошибка проверки CRPT: ${v.message || 'неизвестно'}`);
        }

        if (hasPendingValidation) errs.push('Идёт проверка кодов в CRPT…');

        return errs.slice(0, 2);
    }, [rows, valuesByIndex, validationByCode, hasPendingValidation]);

    const modal = (
        <div className={classes.overlay} onMouseDown={handleOverlayMouseDown} role="dialog" aria-modal="true">
            <div className={classes.modal} onMouseDown={(e) => e.stopPropagation()}>
                <div className={classes.header}>
                    <div className={classes.title}>Маркировка заказа {orderNumber}</div>
                    <button className={classes.closeBtn} type="button" onClick={safeClose} aria-label="Закрыть">
                        ✕
                    </button>
                </div>

                <div className={classes.hint}>
                    1) Кликни по строке товара → активируется поле ввода. <br />
                    2) Отсканируй QR (Enter) - код добавится в “чипы” сверху. <br />
                    3) Сканируй, пока “Сканов: X/Y” не станет X=Y. Как только позиция заполнена, фокус автоматически перейдёт на следующую незаполненную строку.
                    <br />
                    4) Дубликаты/подозрительные коды/лишние коды будут показаны ошибкой - уже добавленные коды при этом не потеряются. <br />
                    5) Backspace в пустом поле отменяет (удаляет) последний добавленный код для этой позиции.
                    <br />
                    6) После внесения всех изменений нажми “Сохранить”.
                </div>

                {error ? <div className={classes.error}>{error}</div> : null}

                {duplicates.size > 0 ? (
                    <div className={classes.error}>
                        Дубликаты кодов: {Array.from(duplicates).slice(0, 3).join(', ')}
                        {duplicates.size > 3 ? ' …' : ''}
                    </div>
                ) : null}

                {validationErrorSummary.map((m) => (
                    <div key={m} className={classes.error}>
                        {m}
                    </div>
                ))}

                {invalidCodes.length > 0 ? (
                    <div className={classes.error}>Есть ошибки в кодах (формат/CRPT/артикул). Исправь или удали перед сохранением.</div>
                ) : null}

                {overfilledRows.length > 0 ? (
                    <div className={classes.error}>Кодов маркировки больше, чем товаров в заказе. Удали лишнее перед сохранением.</div>
                ) : null}

                <div className={classes.tableWrap}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th style={{ width: 48 }}>№</th>
                                <th>Наименование</th>
                                <th style={{ width: 140 }}>Артикул</th>
                                <th style={{ width: 140 }}>Размер/Цвет</th>
                                <th style={{ width: 90 }}>Кол-во</th>
                                <th style={{ width: 360 }}>Маркировка</th>
                                <th style={{ width: 120 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                const codes = normalizeCodesArray(valuesByIndex[r.cartIndex] ?? []);
                                const isActive = activeCartIndex === r.cartIndex;

                                const hasDup = codes.some((c) => duplicates.has(c));
                                const hasBadFormat = codes.some((c) => !looksLikeChz(c));
                                const hasCrptNotFound = codes.some((c) => validationByCode[c]?.status === 'not_found');
                                const hasArticleMismatch = codes.some((c) => validationByCode[c]?.status === 'article_mismatch');
                                const hasCrptError = codes.some((c) => validationByCode[c]?.status === 'error');
                                const hasPending = codes.some((c) => validationByCode[c]?.status === 'pending');

                                const isSuspicious = hasDup || hasBadFormat || hasCrptNotFound || hasArticleMismatch || hasCrptError;

                                const qty = Math.max(0, Math.trunc(r.quantity || 0));

                                const rowTitle = hasArticleMismatch
                                    ? 'Есть коды, не соответствующие артикулу позиции'
                                    : hasCrptNotFound
                                      ? 'Есть коды, которых нет в CRPT базе'
                                      : hasBadFormat
                                        ? 'Есть коды с подозрительным форматом'
                                        : hasCrptError
                                          ? 'Ошибка проверки кода через CRPT'
                                          : 'Кликни, чтобы активировать поле маркировки';

                                return (
                                    <tr
                                        key={r.cartIndex}
                                        className={`${classes.row} ${isActive ? classes.rowActive : ''}`}
                                        onClick={() => activateRow(r.cartIndex)}
                                        title={rowTitle}
                                    >
                                        <td>{i + 1}</td>
                                        <td className={classes.nameCell}>{r.name}</td>
                                        <td className={classes.mono}>{r.articleNumber}</td>
                                        <td>{r.sizeOrColor}</td>
                                        <td className={classes.mono}>{qty}</td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <div className={classes.mono} style={{ opacity: 0.85 }}>
                                                    Сканов: {codes.length}/{qty || 0} {hasPending ? <span style={{ opacity: 0.75 }}>· проверка…</span> : null}{' '}
                                                </div>

                                                {codes.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                        {codes.map((code) => {
                                                            const v = validationByCode[code];
                                                            const isBad =
                                                                duplicates.has(code) ||
                                                                !looksLikeChz(code) ||
                                                                v?.status === 'not_found' ||
                                                                v?.status === 'article_mismatch' ||
                                                                v?.status === 'error';
                                                            const isPend = v?.status === 'pending';

                                                            const chipTitle = (() => {
                                                                if (duplicates.has(code)) return 'Дубликат (код должен быть уникальным в заказе)';
                                                                if (!looksLikeChz(code)) return 'Подозрительный формат кода';
                                                                if (v?.status === 'not_found') return v.message || 'Код не найден в CRPT';
                                                                if (v?.status === 'article_mismatch') return v.message || 'Код относится к другому артикулу';
                                                                if (v?.status === 'error') return v.message || 'Ошибка проверки CRPT';
                                                                if (v?.status === 'pending') return 'Проверяем в CRPT…';
                                                                return code;
                                                            })();

                                                            return (
                                                                <span
                                                                    key={code}
                                                                    className={classes.mono}
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: 6,
                                                                        padding: '2px 6px',
                                                                        borderRadius: 6,
                                                                        border: '1px solid rgba(0,0,0,0.15)',
                                                                        opacity: isPend ? 0.75 : 1,
                                                                        outline: isBad ? '2px solid rgba(220, 53, 69, 0.35)' : 'none',
                                                                    }}
                                                                    title={chipTitle}
                                                                >
                                                                    {code.length > 18 ? `${code.slice(0, 18)}…` : code}
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            removeOneCode(r.cartIndex, code);
                                                                        }}
                                                                        aria-label="Удалить код"
                                                                        style={{
                                                                            border: 'none',
                                                                            background: 'transparent',
                                                                            cursor: 'pointer',
                                                                            padding: 0,
                                                                            lineHeight: 1,
                                                                        }}
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}

                                                <input
                                                    ref={(el) => {
                                                        inputRefs.current[r.cartIndex] = el;
                                                    }}
                                                    className={`${classes.input} ${isSuspicious ? classes.inputWarn : ''}`}
                                                    defaultValue=""
                                                    placeholder={isActive ? 'Сканируйте…' : ''}
                                                    onChange={() => setError('')}
                                                    onKeyDown={(e) => onKeyDown(e, r.cartIndex)}
                                                    onBlur={() => {
                                                        clearPending();
                                                        if (activeCartIndex === r.cartIndex) setActiveCartIndex(null);
                                                    }}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className={classes.clearBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    clearAllCodes(r.cartIndex);
                                                }}
                                                title="Очистить все коды и можно сканировать заново"
                                            >
                                                Очистить
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={classes.footer}>
                    <button type="button" className={classes.secondaryBtn} onClick={safeClose} disabled={saving}>
                        Отмена
                    </button>
                    <button type="button" className={classes.primaryBtn} onClick={handleSave} disabled={!canSave}>
                        {saving ? 'Сохранение…' : hasPendingValidation ? 'Проверяем…' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </div>
    );

    if (typeof document === 'undefined') return null;
    return createPortal(modal, document.body);
};

export default OrderMarkingPopup;
